import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { ReactComponent as DeleteIcon } from '../../../../images/icons/delete_icon.svg';
import NewModal from '../../../Shared/NewModal';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { dimGrey } from '../../../../mainTheme';
import { ReactComponent as DeletionSuccess } from '../../../../images/icons/delete_success_icon.svg';
import { ReactComponent as DeletionFailed } from '../../../../images/icons/delete_failed_icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  clearDeactivateFacilityStatus,
  deactivateLocation,
  deactivateParentFacility,
} from '../../../../store/facility/actionCreators';
import LoadingSpinner from '../../../Shared/LoadingSpinner';

interface DeactivationConfirmationProps {
  type: 'facility' | 'location';
  onClose: () => void;
  onSuccess?: () => void;
  facilityId?: number;
  locationId?: number;
}

const DeactivationConfirmation: React.FC<DeactivationConfirmationProps> = ({
  type,
  onClose,
  onSuccess,
  facilityId,
  locationId,
}) => {
  const dispatch = useDispatch();
  const { deactivateFacilityStatus, isLoading } = useSelector(
    (state: RootState) => state.facility,
  );

  const [confirmationText, setConfirmationText] = useState('');
  const [status, setStatus] = useState<'confirm' | 'success' | 'error'>(
    'confirm',
  );

  const getContent = () => {
    const contents = {
      facility: {
        title: 'Confirm deactivation',
        message:
          'The facility will be deactivated and all of the Contacts and Hubs will be unassigned.',
        successTitle: 'Facility deactivated successfully',
        successMessage:
          "You'll no longer see this facility under the facilities page.",
        errorTitle: 'Deactivation unsuccessful',
        errorMessage: "The facility didn't deactivate, please try again.",
      },
      location: {
        title: 'Confirm deactivation',
        message:
          'The location will be deactivated and all of the Contacts and Hubs will be unassigned.',
        successTitle: 'Location deactivated successfully',
        successMessage:
          "You'll no longer see this location under the parent facility page.",
        errorTitle: 'Deactivation unsuccessful',
        errorMessage: "The location didn't deactivate, please try again.",
      },
    };
    return contents[type];
  };

  const handleModalClose = () => {
    if (status === 'success' && onSuccess) {
      onSuccess();
    } else {
      onClose();
    }
  };

  const handleDeactivate = () => {
    if (confirmationText !== 'deactivate') return;

    if (type === 'facility' && facilityId) {
      dispatch(deactivateParentFacility(facilityId));
    }
    if (type === 'location' && locationId) {
      dispatch(deactivateLocation(locationId));
    }
  };

  useEffect(() => {
    if (deactivateFacilityStatus !== null) {
      setStatus(deactivateFacilityStatus ? 'success' : 'error');
    }
  }, [deactivateFacilityStatus]);

  useEffect(() => {
    return () => {
      dispatch(clearDeactivateFacilityStatus());
    };
  }, [dispatch]);

  const content = getContent();

  if (status === 'success' || status === 'error') {
    return (
      <NewModal
        maxWidth="xs"
        onClose={handleModalClose}
        showCloseX
        title={type === 'facility' ? 'Facility' : 'Location'}
      >
        <StatusMessage
          icon={status === 'success' ? <DeletionSuccess /> : <DeletionFailed />}
          title={
            status === 'success' ? content.successTitle : content.errorTitle
          }
          message={
            status === 'success' ? content.successMessage : content.errorMessage
          }
          onClose={status === 'success' ? onSuccess || onClose : onClose}
          primaryButtonText={status === 'success' ? 'OK' : 'Try again'}
        />
      </NewModal>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <NewModal
      maxWidth="xs"
      onClose={onClose}
      title={type === 'facility' ? 'Facility' : 'Location'}
      showCloseX
      actions={{
        primaryAction: {
          text: 'Deactivate',
          onClick: handleDeactivate,
          disabled: confirmationText !== 'deactivate' || isLoading,
        },
        secondaryAction: {
          text: 'Cancel',
          onClick: onClose,
          disabled: isLoading,
        },
      }}
    >
      <Box>
        <DeleteIcon />
        <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '22px',
              lineHeight: '30px',
              letterSpacing: '0.36px',
            }}
          >
            {content.title}
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '22px',
              letterSpacing: '0.2px',
              color: dimGrey,
            }}
          >
            {content.message}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" py={4} gap={0.5}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '22px',
              letterSpacing: '0.2px',
              color: dimGrey,
              ml: 2,
            }}
          >
            Type the word &quot;deactivate&quot; to confirm.
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            value={confirmationText}
            placeholder='Type "deactivate"'
            onChange={(e) => setConfirmationText(e.target.value)}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>
      </Box>
    </NewModal>
  );
};

export default DeactivationConfirmation;
